/**
 * SRES · Competent Person Approval Modal (Sprint P37m · Feb 2026)
 * ═════════════════════════════════════════════════════════════════
 *
 * Dedicated modal launched from the CPR Dashboard. Captures the CP's
 * formal sign-off for every mandatory phase, persists it via
 * ``POST /api/projects/{pid}/cp-approval``, and immediately re-runs
 * the Publication Gate so the CP sees the readiness verdict without
 * needing to trigger an export.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle, Circle, Shield, X, PencilSimple } from "@phosphor-icons/react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";


const PHASES = [
  { key: "classification_approved",          label: "Resource Classification" },
  { key: "geological_interpretation_approved", label: "Geological Interpretation" },
  { key: "estimation_approved",              label: "Estimation Parameters" },
  { key: "qaqc_approved",                    label: "QA/QC Programme" },
  { key: "variogram_approved",               label: "Variogram Model" },
  { key: "resource_statement_approved",      label: "Resource Statement" },
];


export default function CPApprovalModal({ open, onClose, projectId, project }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    cp_name:            "",
    cp_registration:    "",
    cp_membership:      "",
    reporting_standard: "JORC_2012",
    effective_date:     new Date().toISOString().slice(0, 10),
    density_default:    2.75,
    density_source:     "assumed",
    site_visit_completed: false,
    site_visit_date:    "",
    classification_approved:            false,
    geological_interpretation_approved: false,
    estimation_approved:                false,
    qaqc_approved:                      false,
    variogram_approved:                 false,
    resource_statement_approved:        false,
    approval_status:    "DRAFT",
    approval_notes:     "",
    digital_signature:  "",
  });

  // Load the current approval state
  const stateQ = useQuery({
    queryKey: ["cp-approval", projectId],
    queryFn:  () => api.get(`/projects/${projectId}/cp-approval`).then((r) => r.data),
    enabled:  !!(open && projectId),
  });

  useEffect(() => {
    if (!stateQ.data?.approval) return;
    // Merge existing approval into the form so re-opening shows saved state
    setForm((f) => ({ ...f, ...stateQ.data.approval }));
  }, [stateQ.data]);

  useEffect(() => {
    // If we know the project's CP + density, seed the form once
    if (!open || !project) return;
    setForm((f) => ({
      ...f,
      cp_name:            f.cp_name || project.cp_name || project.cp_qp_names?.[0] || "",
      cp_registration:    f.cp_registration || project.cp_registration || "",
      density_default:    f.density_default || project.density_default || 2.75,
      reporting_standard: f.reporting_standard || project.reporting_standard || "JORC_2012",
    }));
  }, [open, project]);

  const saveMut = useMutation({
    mutationFn: (body) => api.post(`/projects/${projectId}/cp-approval`, body),
    onSuccess:  (r) => {
      const g = r.data?.publication_gate || {};
      qc.invalidateQueries({ queryKey: ["cp-approval", projectId] });
      qc.invalidateQueries({ queryKey: ["publication-gate", projectId] });
      if (g.publish_ready) {
        toast.success("CP approval saved · report is now publish-ready.", { duration: 4500 });
      } else {
        toast.success(
          `CP approval saved · ${g.blocking_count || 0} publication blocker(s) remain.`,
          { duration: 4500 });
      }
      onClose?.();
    },
    onError: (err) => toast.error(err?.response?.data?.message
                                     || err?.response?.data?.detail
                                     || err.message || "Save failed"),
  });

  if (!open) return null;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const requiredMissing = !form.cp_name || !form.effective_date;
  const allPhasesApproved = PHASES.every((p) => form[p.key]);
  const canApproveFinal   = allPhasesApproved
                              && form.cp_name && form.digital_signature
                              && form.density_default > 0;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="cp-approval-modal"
         onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-brand-blue/5 flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-brand-blue/15 text-brand-blue flex items-center justify-center shrink-0">
            <Shield size={18} weight="duotone" />
          </div>
          <div className="flex-1">
            <div className="text-[10.5px] uppercase tracking-widest text-brand-blue font-bold">
              Competent Person Sign-Off
            </div>
            <h2 className="text-[15px] font-black text-slate-900 leading-tight mt-0.5">
              Competent Person Approval — {project?.name || "Project"}
            </h2>
            <div className="text-[11.5px] text-slate-600 mt-1">
              Formal sign-off per JORC 2012 / NI 43-101 / SEC S-K 1300 requirements. This action re-runs the Publication Gate.
            </div>
          </div>
          <button onClick={onClose}
                  className="text-slate-400 hover:text-slate-700 shrink-0"
                  data-testid="cp-approval-close">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Identity */}
          <SectionBlock title="1 · Competent Person Identity">
            <div className="grid grid-cols-2 gap-3">
              <FieldInput label="CP Name *" testid="cp-approval-name"
                          value={form.cp_name}
                          onChange={(v) => setField("cp_name", v)} />
              <FieldInput label="Professional Registration" testid="cp-approval-registration"
                          value={form.cp_registration}
                          onChange={(v) => setField("cp_registration", v)} />
              <FieldInput label="Membership Number" testid="cp-approval-membership"
                          value={form.cp_membership}
                          onChange={(v) => setField("cp_membership", v)} />
              <div>
                <Label className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
                  Reporting Standard *
                </Label>
                <Select value={form.reporting_standard}
                        onValueChange={(v) => setField("reporting_standard", v)}>
                  <SelectTrigger className="h-9 text-[13px] mt-1" data-testid="cp-approval-standard">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="JORC_2012">JORC Code (2012)</SelectItem>
                    <SelectItem value="NI_43_101">NI 43-101</SelectItem>
                    <SelectItem value="SEC_SK_1300">SEC S-K 1300</SelectItem>
                    <SelectItem value="SAMREC">SAMREC 2016</SelectItem>
                    <SelectItem value="CRIRSCO">CRIRSCO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <FieldInput label="Effective Date *" type="date" testid="cp-approval-effective-date"
                          value={form.effective_date}
                          onChange={(v) => setField("effective_date", v)} />
              <FieldInput label="Default Density (t/m³) *" type="number" step="0.01"
                          testid="cp-approval-density"
                          value={form.density_default}
                          onChange={(v) => setField("density_default", parseFloat(v))} />
            </div>
          </SectionBlock>

          {/* Phase approvals */}
          <SectionBlock title="2 · Phase-Level Approvals">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {PHASES.map((p) => (
                <button key={p.key}
                        type="button"
                        onClick={() => setField(p.key, !form[p.key])}
                        className={`flex items-center gap-2.5 rounded-md border px-3 py-2 text-[12px] text-left transition ${
                          form[p.key]
                            ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        }`}
                        data-testid={`cp-approval-${p.key}`}>
                  {form[p.key] ? (
                    <CheckCircle size={16} weight="fill" className="text-emerald-500 shrink-0" />
                  ) : (
                    <Circle size={16} className="text-slate-400 shrink-0" />
                  )}
                  <span className="font-semibold">{p.label}</span>
                </button>
              ))}
            </div>
          </SectionBlock>

          {/* Sign-off */}
          <SectionBlock title="3 · Digital Signature & Status">
            <div className="grid grid-cols-2 gap-3">
              <FieldInput label="Digital Signature *" testid="cp-approval-signature"
                          placeholder="Type your full name to sign"
                          value={form.digital_signature}
                          onChange={(v) => setField("digital_signature", v)} />
              <div>
                <Label className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
                  Approval Status *
                </Label>
                <Select value={form.approval_status}
                        onValueChange={(v) => setField("approval_status", v)}>
                  <SelectTrigger className="h-9 text-[13px] mt-1" data-testid="cp-approval-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DRAFT">DRAFT</SelectItem>
                    <SelectItem value="REVIEW">REVIEW</SelectItem>
                    <SelectItem value="FINAL"
                                 disabled={!canApproveFinal}>
                      FINAL {canApproveFinal ? "" : " (all phases + signature required)"}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
                Approval Notes
              </Label>
              <Textarea rows={3}
                        value={form.approval_notes}
                        onChange={(e) => setField("approval_notes", e.target.value)}
                        className="text-[12.5px] mt-1 resize-none"
                        data-testid="cp-approval-notes"
                        placeholder="Any material scope limitations, dependencies, or reservations" />
            </div>
          </SectionBlock>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 bg-slate-50 px-6 py-3 flex items-center gap-3 flex-wrap">
          <div className={`text-[11px] font-mono ${
            allPhasesApproved ? "text-emerald-700" : "text-amber-700"
          }`}>
            {PHASES.filter((p) => form[p.key]).length} / {PHASES.length} phases approved
          </div>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={onClose}
                  className="h-9" data-testid="cp-approval-cancel">
            Cancel
          </Button>
          <Button size="sm"
                  disabled={requiredMissing || saveMut.isPending}
                  onClick={() => saveMut.mutate(form)}
                  className="h-9 bg-brand-blue text-white hover:bg-brand-blue/90"
                  data-testid="cp-approval-submit">
            <PencilSimple size={13} className="mr-1.5" />
            {saveMut.isPending
              ? "Saving…"
              : form.approval_status === "FINAL"
                ? "Approve FINAL"
                : `Save as ${form.approval_status}`}
          </Button>
        </div>
      </div>
    </div>
  );
}


function SectionBlock({ title, children }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-bold mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}


function FieldInput({ label, value, onChange, type = "text",
                        placeholder, testid, step }) {
  return (
    <div>
      <Label className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </Label>
      <Input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        type={type} placeholder={placeholder} step={step}
        className="h-9 text-[13px] mt-1"
        data-testid={testid}
      />
    </div>
  );
}
